import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	DaysOfWeek,
	DefaultDeliveryTime,
	DefaultTimezone,
	ScheduleFrequencies,
} from "constants/reportSubscriptions";
import { formatScheduleSummary, suggestSubscriptionName } from "helpers/reportSubscriptions";

const AllDays = DaysOfWeek.map((d) => d.value);

const buildInitialState = (initial, fallbackEmail = "") => {
	const seed = initial || {};
	const frequency = seed.frequency ?? ScheduleFrequencies.Weekly;
	let daysOfWeek = seed.daysOfWeek ?? [];
	if (frequency === ScheduleFrequencies.Weekly && !daysOfWeek.length) {
		daysOfWeek = ["Monday"];
	}
	return {
		name: seed.name ?? "",
		reportType: seed.reportType ?? "",
		frequency,
		daysOfWeek,
		timeOfDay: seed.timeOfDay ?? DefaultDeliveryTime,
		timezone: seed.timezone ?? DefaultTimezone,
		deliveryEmail: seed.deliveryEmail ?? fallbackEmail,
	};
};

// Single source of truth for the subscribe form. Holds field values, exposes
// per-field setters, and derives the human-readable summary line shown at the
// bottom of the modal. Validation lives here too so the modal stays a thin
// composition of presentational pieces.
export const useScheduleForm = ({
	initialValues,
	fallbackEmail,
	availableReports,
	currentFilters = [],
} = {}) => {
	const [values, setValues] = useState(() => buildInitialState(initialValues, fallbackEmail));

	// Tracks whether the user has typed in the Name field. Once they've touched
	// it (or it was pre-filled from initialValues), auto-suggestions stop
	// overwriting their input.
	const nameTouchedRef = useRef(!!initialValues?.name);

	const setField = useCallback((field, value) => {
		if (field === "name") {
			nameTouchedRef.current = true;
		}
		setValues((prev) => ({ ...prev, [field]: value }));
	}, []);

	const toggleDay = useCallback((day) => {
		setValues((prev) => {
			const has = prev.daysOfWeek.includes(day);
			return {
				...prev,
				daysOfWeek: has
					? prev.daysOfWeek.filter((d) => d !== day)
					: [...prev.daysOfWeek, day],
			};
		});
	}, []);

	// `daysOfWeek` always reflects the user's *weekly* picker selection so it
	// survives toggling Daily↔Weekly without losing what they had picked.
	// Daily's "every day" semantics are derived via `effectiveDays`.
	const setFrequency = useCallback((frequency) => {
		setValues((prev) => {
			if (frequency === ScheduleFrequencies.Weekly && !prev.daysOfWeek.length) {
				return { ...prev, frequency, daysOfWeek: ["Monday"] };
			}
			return { ...prev, frequency };
		});
	}, []);

	const effectiveDays = useMemo(
		() =>
			values.frequency === ScheduleFrequencies.Daily
				? [...AllDays]
				: values.daysOfWeek,
		[values.frequency, values.daysOfWeek]
	);

	const selectedReport = useMemo(
		() =>
			availableReports?.find((r) => r.reportType === values.reportType),
		[availableReports, values.reportType]
	);

	const reportName = selectedReport?.name || "";

	const summary = useMemo(
		() =>
			formatScheduleSummary({
				name: values.name,
				reportName,
				frequency: values.frequency,
				days: effectiveDays,
				time: values.timeOfDay,
				timezone: values.timezone,
				email: values.deliveryEmail || fallbackEmail,
			}),
		[reportName, values, effectiveDays, fallbackEmail]
	);

	const isValid = useMemo(
		() =>
			!!values.name?.trim() &&
			!!values.reportType &&
			!!values.timeOfDay &&
			!!values.timezone &&
			effectiveDays.length > 0,
		[values, effectiveDays]
	);

	useEffect(() => {
		if (nameTouchedRef.current) return;
		const suggestion = suggestSubscriptionName({
			reportName,
			filters: currentFilters,
		});
		if (!suggestion) return;
		setValues((prev) =>
			prev.name === suggestion ? prev : { ...prev, name: suggestion }
		);
	}, [reportName, currentFilters]);

	return {
		values,
		effectiveDays,
		setField,
		setFrequency,
		toggleDay,
		summary,
		isValid,
		reportName,
	};
};
